import React, { useEffect, useRef } from "react";
import { loadLeaflet } from "../lib/leaflet";

/**
 * A small OpenStreetMap (Leaflet) view showing the target property + comparables.
 * Leaflet is fetched on demand by lib/leaflet.js rather than blocking every
 * page in the app on a CDN script most of them never use — zero npm cost still.
 */
export default function SalesMap({ target, sales = [] }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !target?.latitude) return;
    let cancelled = false;

    loadLeaflet().then((L) => {
      // The component can unmount, or its props change, while the CDN request
      // is in flight; building a map into a detached node leaks it.
      if (cancelled || !containerRef.current) return;

      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      const map = L.map(containerRef.current, {
        center: [target.latitude, target.longitude],
        zoom: 15,
        scrollWheelZoom: false,
      });
      mapRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);

      // Target property marker (dark charcoal)
      const targetIcon = L.divIcon({
        className: "wb-map-target",
        html: `<div style="width:22px;height:22px;border-radius:50%;background:#1A1A1A;border:3px solid #FDFBF7;box-shadow:0 0 0 2px #1A1A1A;"></div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });
      const targetMarker = L.marker([target.latitude, target.longitude], { icon: targetIcon })
        .addTo(map)
        .bindPopup(`<strong>Your property</strong><br/>${target.label || ""}`);

      // Comparable markers (brass accent)
      const compIcon = L.divIcon({
        className: "wb-map-comp",
        html: `<div style="width:14px;height:14px;border-radius:50%;background:#B89B5E;border:2px solid #FDFBF7;box-shadow:0 0 0 1px #B89B5E;"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });

      const bounds = [[target.latitude, target.longitude]];
      sales.forEach((s) => {
        if (s.latitude == null || s.longitude == null) return;
        const addr = [s.paon, s.street].filter(Boolean).join(" ");
        const price = new Intl.NumberFormat("en-GB").format(s.price || 0);
        L.marker([s.latitude, s.longitude], { icon: compIcon })
          .addTo(map)
          .bindPopup(`<strong>£${price}</strong><br/>${addr}<br/><em>${(s.date || "").slice(0, 16)}</em>`);
        bounds.push([s.latitude, s.longitude]);
      });

      if (bounds.length > 1) {
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 });
      }

      setTimeout(() => map.invalidateSize(), 100);
    }).catch(() => {
      // The CDN is unreachable. The surrounding report still stands on its
      // own; a missing map is not worth an unhandled rejection in the console.
    });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [target, sales]);

  if (!target?.latitude) return null;

  return (
    <div className="border border-border rounded-sm overflow-hidden" data-testid="sales-map-wrapper">
      <div ref={containerRef} data-testid="sales-map" style={{ height: 320, width: "100%" }} />
      <div className="flex items-center gap-5 px-4 py-3 text-xs text-muted-foreground bg-card border-t border-border">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-primary" /> Your property
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-accent" /> Recent nearby sales
        </div>
      </div>
    </div>
  );
}
